import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../exam.js';

/**
 * Atomically increments exam.totalCost AND the matching exam.costBySource entry by an arbitrary delta —
 * the shared write shared by `addCostToExam` (one call, delta = one response's cost) and
 * `createCostTracker.flush()` (one call per accumulated source, delta = that source's batched total).
 */
export async function applyCostDelta(
  examId: string | undefined,
  source: string,
  costDelta: number,
  callCountDelta: number,
): Promise<number> {
  if (!examId || !Number.isFinite(costDelta) || costDelta <= 0) return 0;

  try {
    const examCollection = await getCollection(Exam as any);

    // Common case: this source already has an entry (from an earlier call on this exam) — increment it and
    // the aggregate total in one atomic op. Works unchanged on an exam that predates `costBySource` entirely,
    // since the query below simply won't match and falls through to the create-branch.
    const increment = () =>
      examCollection.updateOne(
        { _id: examId, 'costBySource.source': source } as any,
        {
          $inc: {
            totalCost: costDelta,
            'costBySource.$.totalCost': costDelta,
            'costBySource.$.callCount': callCountDelta,
          },
        } as any,
      );

    let result = await increment();

    if (result.modifiedCount !== 1) {
      // No entry for this source yet — new source, or an exam that predates this field entirely (old exams
      // keep working here: `costBySource` absent is exactly "no element has this source", so the guard
      // below is satisfied and the array is created on the spot). Guarded with `{$ne: source}` so the push
      // only succeeds if STILL absent at the exact moment this update executes — race-proof against a
      // concurrent first-write for the same source, since MongoDB serializes writes to one document.
      const pushResult = await examCollection.updateOne(
        { _id: examId, 'costBySource.source': { $ne: source } } as any,
        {
          $inc: { totalCost: costDelta },
          $push: {
            costBySource: { _class: 'CostBySourceEntry', source, totalCost: costDelta, callCount: callCountDelta },
          },
        } as any,
      );

      if (pushResult.modifiedCount === 1) {
        result = pushResult;
      } else {
        // Lost the race: another call's push landed between the two attempts above. Its entry exists now,
        // so retry the increment instead of trying to push a duplicate.
        result = await increment();
      }
    }

    if (result.modifiedCount === 1) {
      logger.info({ costFormatted: costDelta.toFixed(6), context: source }, 'Cost persisted to exam.totalCost');
      return costDelta;
    } else {
      logger.warn({ examId, context: source }, 'Update did not modify any documents');
      return 0;
    }
  } catch (error) {
    logger.error({ err: error, examId, costDelta, source }, `[${source}] ❌ Failed to persist cost to database`);
    return 0;
  }
}

/**
 * Extracts cost from LLM response and atomically updates exam.totalCost AND the per-source breakdown
 * (exam.costBySource) in the database. Safe: validates cost is positive and finite before persisting.
 *
 * @param response - LLM response object with usage.cost field
 * @param examId - The exam ID to update
 * @param source - Label for logging AND for the per-source cost breakdown (e.g., 'defaultGrading', 'msGen')
 * @returns The cost amount that was persisted, or 0 if no valid cost
 */
export async function addCostToExam(
  response: any,
  examId: string | undefined,
  source = 'unknown',
): Promise<number> {
  if (!examId) {
    logger.debug({ context: source }, 'No examId provided, skipping cost tracking');
    return 0;
  }

  const usage = response?.usage;
  const cost = Number(
    typeof usage === 'number'
      ? usage
      : typeof usage?.cost === 'number' || typeof usage?.cost === 'string'
        ? usage.cost
        : typeof response?.cost === 'number' || typeof response?.cost === 'string'
          ? response.cost
          : 0,
  );

  if (!Number.isFinite(cost) || cost <= 0) {
    logger.debug({ cost, context: source }, 'No valid cost in response');
    return 0;
  }

  return applyCostDelta(examId, source, cost, 1);
}
